import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import { useStore } from "../contexts/StoreContext";
import { usePlanAlert } from "../contexts/PlanAlertContext";
import usePlanTier, { tierAllows } from "../hooks/usePlanTier";
import { useIntegrationsData } from "../hooks/useIntegrationsData";
import IntegrationDrawer from "./integrations/IntegrationDrawer";
import { AvailableCard, ConnectedCard } from "./integrations/components/IntegrationCard";
import { ALL_TAGS, TAG_LABEL_KEYS, providerByKey } from "./integrations/providers";

// Integrations: what is connected, what can be, and one drawer for both.
//
// ?tag=<tag> filters the Available list (Marketing links here with
// ?tag=email|sms). ?edit=<id> opens any of the store's integrations in the
// drawer, so the old /integration/:id/configure links land here (decision
// D1). The list shows one card per type, but the API allows two of a type, so
// ?edit is resolved against every record, not just the cards. Opening and
// closing the drawer replace the history entry, so Back leaves the page.
//
// Plan gate (decision D3): connecting needs the Advanced tier, the same rule
// the dashboard uses (a store on trial counts as Advanced). A store below it
// still opens, edits and disconnects what it already has connected.
export default function Integrations() {
  const { t } = useLanguage();
  const { isStoreLoading, selectedStore } = useStore();
  const { showPlanAlert } = usePlanAlert();
  const tier = usePlanTier(selectedStore);
  // No lock (and no banner) until the store is known: with no store yet the
  // tier reads "basic", which flashed the banner for Advanced/trial stores.
  const storeReady = !isStoreLoading && !!selectedStore;
  const locked = storeReady && !tierAllows(tier, "advanced");
  const [searchParams, setSearchParams] = useSearchParams();
  const { storeId, integrations, connected, available, isLoading } = useIntegrationsData();

  const tagParam = searchParams.get("tag");
  const selectedTag = tagParam && ALL_TAGS.includes(tagParam) ? tagParam : "all";
  const editId = searchParams.get("edit");
  const [createKey, setCreateKey] = useState(null);

  const editRecord = editId
    ? integrations.find((integration) => String(integration.id) === editId)
    : null;
  const editProvider = providerByKey(editRecord?.integration_type);
  const editing =
    editRecord && editProvider && !editProvider.soon
      ? { provider: editProvider, integration: editRecord }
      : null;

  // A stale or foreign ?edit= id: drop it once the list has loaded.
  const editFound = !!editing;
  useEffect(() => {
    if (editId && !isLoading && storeId && !editFound) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("edit");
          return next;
        },
        { replace: true },
      );
    }
  }, [editId, isLoading, storeId, editFound, setSearchParams]);

  const target = editing
    ? { mode: "edit", key: editing.provider.key, id: editing.integration.id }
    : createKey
      ? { mode: "create", key: createKey }
      : null;
  // Keep rendering the last target while the drawer slides out.
  const lastTarget = useRef(null);
  if (target) lastTarget.current = target;
  // What the drawer shows right now, for requests that finish later.
  const currentTarget = useRef(null);
  currentTarget.current = target;

  const openEdit = (id) => {
    setCreateKey(null);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("edit", String(id));
        return next;
      },
      { replace: true },
    );
  };

  const closeDrawer = useCallback(() => {
    setCreateKey(null);
    setSearchParams(
      (prev) => {
        if (!prev.has("edit")) return prev;
        const next = new URLSearchParams(prev);
        next.delete("edit");
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // A save, connect or disconnect closes the drawer only if it still shows
  // the integration that request was for.
  const closeIfShowing = useCallback(
    (expected) => {
      const current = currentTarget.current;
      if (
        current &&
        current.mode === expected.mode &&
        current.key === expected.key &&
        (expected.mode !== "edit" || String(current.id) === String(expected.id))
      ) {
        closeDrawer();
      }
    },
    [closeDrawer],
  );

  const pickTag = (tag) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tag === "all") next.delete("tag");
      else next.set("tag", tag);
      return next;
    });

  const connect = (provider) => {
    if (provider.soon) return;
    if (locked) {
      showPlanAlert({
        title: t("common.planAlertTitle"),
        description: t("common.planAlertDescription", { plan: "Advanced" }),
        plan: "Advanced",
      });
      return;
    }
    setCreateKey(provider.key);
  };

  // "Coming soon" providers last.
  const shown = available
    .filter((p) => selectedTag === "all" || p.tags.includes(selectedTag))
    .sort((a, b) => Number(!!a.soon) - Number(!!b.soon));

  const loading = !storeReady || isLoading;

  return (
    <div className="flex max-w-[1240px] flex-col gap-5">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 sm:text-[28px]">
          {t("integrations.list.title")}
        </h1>
        <p className="text-[15px] text-gray-700">{t("integrations.list.description")}</p>
      </header>

      {locked && (
        <section
          aria-labelledby="integrations-plan-title"
          className="flex flex-wrap items-center gap-4 rounded-[14px] border border-indigo-200 bg-indigo-50 px-5 py-[18px]"
        >
          <span
            aria-hidden="true"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-white text-indigo-700"
          >
            <LockClosedIcon className="h-5 w-5" />
          </span>
          <span className="flex min-w-0 flex-[1_1_320px] flex-col gap-0.5">
            <h2 id="integrations-plan-title" className="text-base font-semibold text-indigo-950">
              {t("integrations.locked.title")}
            </h2>
            <span className="text-sm leading-normal text-indigo-800">
              {connected.length ? t("integrations.locked.bodyConnected") : t("integrations.locked.body")}
            </span>
          </span>
          <Link
            to="/pricing"
            className="inline-flex min-h-11 items-center rounded-[10px] bg-indigo-600 px-[18px] text-sm font-semibold text-white hover:bg-indigo-700"
          >
            {t("common.viewPlans")}
          </Link>
        </section>
      )}

      {loading ? (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3" aria-busy="true">
          <p className="sr-only">{t("common.loading")}</p>
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-40 animate-pulse rounded-[14px] bg-gray-100" />
          ))}
        </div>
      ) : (
        <>
          {connected.length > 0 && (
            <section aria-labelledby="integrations-connected-title" className="flex flex-col gap-2.5">
              <h2 id="integrations-connected-title" className="text-[17px] font-semibold text-gray-900">
                {t("integrations.connected")}
              </h2>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,340px),1fr))] gap-3">
                {connected.map(({ provider, integration }) => (
                  <ConnectedCard
                    key={integration.id}
                    provider={provider}
                    integration={integration}
                    onConfigure={() => openEdit(integration.id)}
                  />
                ))}
              </div>
            </section>
          )}

          <section aria-labelledby="integrations-available-title" className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 id="integrations-available-title" className="text-[17px] font-semibold text-gray-900">
                {t("integrations.available")}
              </h2>
              <div
                role="group"
                aria-label={t("integrations.filter.label")}
                className="flex flex-wrap gap-1.5"
              >
                {["all", ...ALL_TAGS].map((tag) => {
                  const on = selectedTag === tag;
                  return (
                    <button
                      key={tag}
                      type="button"
                      aria-pressed={on}
                      onClick={() => pickTag(tag)}
                      className={`min-h-9 rounded-full border px-3 text-[13px] font-medium ${
                        on
                          ? "border-gray-900 bg-gray-900 text-white"
                          : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {tag === "all"
                        ? t("integrations.filter.allTags")
                        : t(`integrations.tags.${TAG_LABEL_KEYS[tag]}`)}
                    </button>
                  );
                })}
              </div>
            </div>
            {shown.length ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,280px),1fr))] gap-3">
                {shown.map((provider) => (
                  <AvailableCard
                    key={provider.key}
                    provider={provider}
                    locked={locked}
                    onConnect={() => connect(provider)}
                  />
                ))}
              </div>
            ) : (
              <p className="rounded-[14px] border border-dashed border-gray-300 px-4 py-6 text-center text-sm text-gray-600">
                {t("integrations.filter.empty")}
              </p>
            )}
          </section>
        </>
      )}

      <IntegrationDrawer
        open={!!target}
        target={target || lastTarget.current}
        storeId={storeId}
        onClose={closeDrawer}
        onDone={closeIfShowing}
      />
    </div>
  );
}
